import {
  ButtonLink,
  Divider,
  MarkdownRenderer,
  SideDrawer,
  SidedrawerProps,
  Spec,
  SpecList,
  Typography,
  UploadedFile,
} from '@maia/ui';
import { useDrawerStore } from '../../../stores/drawer.store';
import { downloadEncodedDocument, formatDate, getFullName } from '@maia/utils';
import { useId } from 'react';
import { useMutation } from '@tanstack/react-query';
import { service } from '@maia/attachments';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { IconAngleRight } from '@maia/icons';

type Props = Pick<SidedrawerProps, 'onOpenChange' | 'portalRef'>;

export function TaskSidedrawer(props: Props) {
  const id = useId();
  const { ...rest } = props;
  const { state, closeDrawer } = useDrawerStore();
  const { t } = useTranslation('system');

  const { mutateAsync } = useMutation({
    mutationFn: (guid: string) => service.find(guid),
  });

  async function handleDownload(guid: string) {
    const documentData = await mutateAsync(guid);

    if (documentData) {
      return downloadEncodedDocument(documentData);
    }
  }

  return (
    <SideDrawer
      {...rest}
      title="Bekijk taak"
      className="w-full pb-32 max-w-[var(--max-side-drawer-width)]"
      isOpen={state?.isOpen && state.info?.type === 'task'}
      onOpenChange={closeDrawer}
    >
      {state?.info?.type === 'task' ? (
        <div className="flex flex-col mt-32 gap-y-32">
          <div className="flex flex-col">
            <Typography
              variant="body-s"
              weight="medium"
              className="text-grayscale600 mt-12"
            >
              {state?.info.data.title}
            </Typography>
            <SpecList className="mt-12">
              <Spec
                className="grid grid-cols-2"
                title="Gekoppeld item"
                value={
                  <Link
                    to={`/${state.info.data.linkedItem.type}s/$guid`}
                    params={{ guid: state.info.data.linkedItem.guid }}
                  >
                    <Typography
                      variant="body-xs"
                      weight="medium"
                      className="text-info300 text-nowrap"
                    >
                      {t(`linkedItem.${state.info.data.linkedItem.type}`)} #
                      {state.info.data.linkedItem.label}
                    </Typography>
                  </Link>
                }
              />
              <Spec
                className="grid grid-cols-2"
                title="Aangemaakt op"
                value={formatDate(state?.info.data.createdAt)}
              />

              <Spec
                className="grid grid-cols-2"
                title="Aangemaakt door"
                value={getFullName(state?.info.data.createdBy)}
              />
            </SpecList>
          </div>

          <Divider />

          <div className="overflow-y-auto max-h-[300px]">
            <MarkdownRenderer markdown={state?.info.data.description} />
          </div>

          {state?.info.data.attachments.length > 0 && (
            <div className="overflow-y-auto max-h-[300px] space-y-8">
              {state?.info.data.attachments.map((attachment, key) => (
                <UploadedFile
                  key={`note-sidedrawer-${id}-uploaded-file-${attachment.filename}-${key}`}
                  name={attachment.filename}
                  mimetype={attachment.mimetype}
                  action="download"
                  onDownload={async () => await handleDownload(attachment.guid)}
                />
              ))}
            </div>
          )}

          <ButtonLink
            to={`/${state.info.data.linkedItem.type}s/$guid`}
            params={{ guid: state.info.data.linkedItem.guid }}
            size="small"
            iconRight={IconAngleRight}
            className="w-fit"
          >
            Naar taak
          </ButtonLink>
        </div>
      ) : null}
    </SideDrawer>
  );
}
